import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { BrandLockup } from "@/components/Logo";
import { Reveal } from "@/components/Kinetic";
import { SITE } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About Us" },
  { to: "/reservation", label: "Book Now" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative overflow-hidden border-t border-gold/15 bg-obsidian-900">
      <Reveal className="pointer-events-none select-none px-5 pt-16 sm:px-8">
        <p className="outline-word text-center font-display text-[22vw] font-semibold leading-[0.8] lg:text-[16rem]">
          NANAK
        </p>
      </Reveal>

      <div className="relative mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 lg:px-12">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <BrandLockup className="h-24 w-auto" data-testid="footer-brand-logo" />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-silver/80">
              Premium, professionally operated luxury ground transportation.
              Discreet chauffeurs, immaculate late-model SUVs, and arrivals timed
              to the minute — every single ride.
            </p>
            <Link
              to="/reservation"
              data-testid="footer-reserve-button"
              className="group mt-8 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-obsidian-900"
            >
              Reserve Now
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
              Explore
            </p>
            <ul className="mt-6 space-y-3">
              {LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-sm text-ivory-dim transition-colors duration-300 hover:text-gold"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
              Dispatch
            </p>
            <ul className="mt-6 space-y-4 text-sm text-ivory-dim">
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-gold" />
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email-link"
                  className="transition-colors duration-300 hover:text-gold"
                >
                  {SITE.email}
                </a>
              </li>
              {SITE.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-gold" />
                  <a
                    href={SITE.phoneHref}
                    data-testid="footer-phone-link"
                    className="transition-colors duration-300 hover:text-gold"
                  >
                    {SITE.phone}
                  </a>
                </li>
              )}
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span>{SITE.area}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-gold/10 pt-8 sm:flex-row">
          <p className="font-mono text-[11px] tracking-[0.18em] text-ivory-dim/60">
            © {year} {SITE.name} · ALL RIGHTS RESERVED
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy"
              data-testid="footer-link-privacy"
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-ivory-dim/70 transition-colors duration-300 hover:text-gold"
            >
              Privacy Policy
            </Link>
            <span className="text-ivory-dim/30">·</span>
            <Link
              to="/terms"
              data-testid="footer-link-terms"
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-ivory-dim/70 transition-colors duration-300 hover:text-gold"
            >
              Terms of Service
            </Link>
          </div>
          <p className="font-mono text-[11px] tracking-[0.18em] text-ivory-dim/60">
            POWERED BY <span className="text-gold/70">LIMOANYWHERE</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
